// ─────────────────────────────────────────────
// autoAnnotate.js
// Pre-highlights all BioRECIPE field values
// found in a statement string.
// ─────────────────────────────────────────────

const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * Check whether the character at a given position is a word character
 * (letter, digit, underscore, or common bio chars like hyphen in gene names).
 */
function isWordChar(ch) {
  if (!ch) return false;
  return /[\w]/.test(ch);
}

/**
 * Find all whole-word occurrences of a substring (case-insensitive) in a text.
 * "Whole word" means the match must NOT be surrounded by word characters,
 * so "other" won't match inside "radiotherapy" and "protein" won't match
 * inside "glycoprotein".
 *
 * Returns array of { start, end } offsets.
 */
function findAllOccurrences(text, substring) {
  if (!text || !substring) return [];
  const matches = [];
  const lower = text.toLowerCase();
  const sub = substring.toLowerCase().trim();
  if (!sub) return [];

  let pos = 0;
  while (pos < lower.length) {
    const idx = lower.indexOf(sub, pos);
    if (idx === -1) break;

    const end = idx + sub.length;

    // Check word boundaries:
    // - char before match (if any) must NOT be a word character
    // - char after match (if any) must NOT be a word character
    const charBefore = idx > 0 ? lower[idx - 1] : null;
    const charAfter = end < lower.length ? lower[end] : null;

    if (!isWordChar(charBefore) && !isWordChar(charAfter)) {
      matches.push({ start: idx, end });
    }

    pos = idx + 1;
  }
  return matches;
}

/**
 * Check if a candidate range overlaps with any existing annotation.
 */
function hasOverlap(annotations, start, end) {
  return annotations.some((a) => start < a.end && end > a.start);
}

/**
 * Build initial annotations for all BioRECIPE field values found in a statement.
 *
 * @param {string}   statement  – the full statement text
 * @param {Object}   row        – the full interaction row object
 * @param {string[]} labels     – the labels array passed to TextAnnotatorModal
 *                                (should be BIO_ANNOTATION_LABELS)
 * @param {Object}   fieldLabelMap – maps row field names to label names, e.g.:
 *                    { "Regulator Name": "Regulator Name", "Sign": "Sign", ... }
 *                    If a field's label isn't in `labels`, it's skipped.
 * @returns {Array} annotation objects ready for initialAnnotations prop
 */
export function buildAutoAnnotations(
  statement,
  row = {},
  labels = [],
  fieldLabelMap = {},
) {
  if (!statement) return [];

  const annotations = [];
  const now = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Sort fields by value length descending — longer matches first to avoid
  // a short value (e.g. "protein") consuming a span that a longer, more
  // specific value (e.g. "protein kinase") should own.
  const entries = Object.entries(fieldLabelMap)
    .map(([field, labelName]) => ({
      field,
      labelName,
      value: (row[field] || "").trim(),
      labelIdx: labels.indexOf(labelName),
    }))
    .filter((e) => e.value && e.value.length >= 2 && e.labelIdx >= 0)
    .sort((a, b) => b.value.length - a.value.length);

  for (const { field, labelName, value, labelIdx } of entries) {
    const matches = findAllOccurrences(statement, value);

    for (const m of matches) {
      if (!hasOverlap(annotations, m.start, m.end)) {
        annotations.push({
          id: generateId(),
          start: m.start,
          end: m.end,
          text: statement.slice(m.start, m.end),
          note: `${field}: ${value}`,
          label: labelName,
          colorIdx: labelIdx,
          timestamp: now,
        });
      }
    }
  }

  // Sort by start offset
  annotations.sort((a, b) => a.start - b.start);

  return annotations;
}